<script lang="ts" setup>
import {
  createMateSprite,
  deleteMateSprite,
  updateMateSprite,
} from "@/apis/script-game";
import { Sex } from "@/enums/characterGeneratorEnums";
import { MateSpriteDirection } from "@/enums/game";
import { useDungeon } from "@/stores/dungeon";
import { useGlobalModal } from "@/stores/globalModal";
import { useMate } from "@/stores/mate";
import { MateSprite, MateSpriteStatus } from "@/types/game";
import { SelectedItems, SheetDefinition } from "@/types/sheet-definiation";
import { useName, usePreviewImg } from "@/use/common";
import { useSpriteSheetExport } from "@/use/useSpriteSheetExport";
import { useRouteQuery } from "@vueuse/router";
import { showConfirmDialog, showLoadingToast, showSuccessToast } from "vant";
import { onMounted, onUnmounted } from "vue";
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
// Props & Emit
const props = withDefaults(
  defineProps<{
    enableSelectionMode?: boolean;
    onBackClick?: () => void;
    showHeader?: boolean;
  }>(),
  {
    enableSelectionMode: false,
    showHeader: true,
  }
);

const emit = defineEmits<{
  (e: "onConfirm", spriteId: number, mateUUID: string): void;
}>();

// Stores
const mateStore = useMate();
const router = useRouter();
const loading = ref(false);
const store = useDungeon();
const modalStore = useGlobalModal();
const { t } = useI18n();

// State
const uuid = useRouteQuery("uuid", "");

const spriteList = ref<MateSprite[]>([]);
const editingSprite = ref<MateSprite | undefined>();
const selectedSprite = ref<MateSprite | undefined>();

// Lifecycle
let timer: NodeJS.Timeout | null = null;
const initPage = async (force = false, withoutLoading = false) => {
  if (!withoutLoading) {
    loading.value = true;
  }
  const res = await store.fetchMateSpriteList(
    mateStore.currentMate!.id!,
    force
  );
  spriteList.value = res;

  // 有正在加载的sprite,
  const temp = res.filter((v) => v.status === MateSpriteStatus.Processing);
  if (temp.length > 0) {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    timer = setTimeout(() => {
      initPage(true, true);
    }, 5000);
  }

  // 设置默认选中
  const defaultSprite = spriteList.value.find((sprite) => sprite.isDefault);
  if (defaultSprite) {
    selectedSprite.value = defaultSprite;
  }
  if (!withoutLoading) {
    loading.value = false;
  }
};

watch(uuid, () => {
  if (!uuid.value) return;
  const mate = mateStore.mateMap.get(uuid.value as string)!;
  if (mate.id !== mateStore.currentMate?.id && mate) {
    mateStore.changeCurrentMate(mate.id);
  }
});

watch(
  () => mateStore.currentMate,
  () => {
    initPage(true);
  },
  { flush: "post" }
);

const onSpriteClick = (sprite?: MateSprite, isCreate = false) => {
  if (!sprite) return clickAddSprite();

  if (editingSprite.value?.id !== sprite.id && !isCreate) {
    editingSprite.value = sprite;
  } else {
    editingSprite.value = sprite;

    router.push({
      name: "mateCreate",
      query: {
        spriteId: sprite.id,
      },
    });
  }
};
const { mergeAndUploadSpritesheet } = useSpriteSheetExport();

const onSelectionConfirm = async () => {
  // 1. 若尚未生成Spritesheet图片，则立刻生成
  if (!selectedSprite.value?.frameMediaId) {
    const loading = showLoadingToast({});
    const prompt: {
      sex: Sex;
      items: SelectedItems;
    } = JSON.parse(selectedSprite.value!.prompt!);
    const defList: SheetDefinition[] = Object.values(prompt.items).flatMap(
      (item) => Object.values(item)
    );
    await mergeAndUploadSpritesheet(defList, prompt.sex);
    loading.close();
  }

  emit("onConfirm", selectedSprite.value!.id!, mateStore.currentMate!.uuid!);
};

const clickAddSprite = async () => {
  // editingSprite.value = undefined;

  const loading = showLoadingToast(`创建中...`);
  const { item } = await createMateSprite({
    mateId: mateStore.currentMate!.id!,
  });
  loading.close();
  onSpriteClick(item, true);
  return;

  // const hasSprite = mateStore.currentMate?.spritezz?.length;

  // modalStore.openCustomGlobalActionSheet({
  //   title: "请选择创建形象的方式",
  //   list: [
  //     // {
  //     //   content: "从预设创建",
  //     //   callback: () => {
  //     //     router.push({
  //     //       name: "mateCreate",
  //     //       query: {
  //     //         mateType: mateStore.currentMate!.type!,
  //     //         isPreset: 1,
  //     //       },
  //     //     });
  //     //   },
  //     // },
  //     {
  //       content: hasSprite ? "从参考图创建" : "从预设/参考图创建",
  //       callback: () => {
  //         router.push({
  //           name: "mateCreate",
  //           query: {
  //             mateType: mateStore.currentMate!.type!,
  //             isImage: hasSprite ? 1 : undefined,
  //           },
  //         });
  //       },
  //     },
  //     {
  //       content: "自定义形象",
  //       callback: async () => {
  //         const loading = showLoadingToast(`创建中...`);
  //         const { item } = await createMateSprite({
  //           mateId: mateStore.currentMate!.id!,
  //         });
  //         loading.close();
  //         onSpriteClick(item, true);
  //       },
  //     },
  //   ],
  // });
};

const setDefault = async () => {
  await updateMateSprite(editingSprite.value!.id!, {
    isDefault: true,
  });
  mateStore.fetchMateList(false);
  editingSprite.value!.isDefault = true;
};

const showMediaPreviewPopup = ref(false);

const deleteSprite = async () => {
  showConfirmDialog({
    title: t("characterGenerator.messages.sureDeleteSprite"),
    confirmButtonText: t("Buttons.delete"),
    cancelButtonText: t("Buttons.cancel"),
  })
    .then(async () => {
      const loading = showLoadingToast(`删除中...`);
      try {
        await deleteMateSprite(editingSprite.value!.id!);

        const [res] = await Promise.all([
          store.fetchMateSpriteList(mateStore.currentMate!.id!, true),
          mateStore.fetchMateList(false),
        ]);

        spriteList.value = res;
        loading.close();
        editingSprite.value = undefined;
        showSuccessToast("删除成功");
      } catch {
        loading.close();
      }
    })
    .catch(() => {
      // ignore
    });
};

onMounted(async () => {
  if (uuid.value && mateStore.currentMate?.uuid !== uuid.value) {
    const mate = mateStore.mateMap.get(uuid.value as string)!;
    if (mate) mateStore.changeCurrentMate(mate.id);
    uuid.value = "";
  }
  await initPage(true);

  if (spriteList.value.length) {
    const defaultSpriteIdx = spriteList.value.findIndex(
      (sprite) => sprite.isDefault
    );
    if (defaultSpriteIdx !== -1) {
      const defaultSprite = spriteList.value[defaultSpriteIdx];
      spriteList.value.splice(defaultSpriteIdx, 1);
      spriteList.value.unshift(defaultSprite);
    }
    if (spriteList.value[0]) onSpriteClick(spriteList.value[0]);
  }
});

onUnmounted(() => {
  if (timer) {
    clearTimeout(timer);
  }
});
</script>
<template>
  <div class="grid overflow-hidden h-full grid-rows-[auto_1fr]">
    <ThreadsNav
      v-if="showHeader"
      show-back
      :go-back="props.onBackClick"
      :show-center="false"
    >
      <template #left>
        <button
          v-if="!editingSprite?.isDefault"
          class="btn btn-xs rounded btn-outline"
          @click="setDefault"
        >
          {{ $t("Buttons.setDefault") }}
        </button>
        <div
          v-else
          class="flex items-center gap-1 text-xs text-base-content/50"
        >
          <i class="iconify mingcute--check-circle-line" />
          {{ $t("Message.default") }}
        </div>
      </template>
      <!-- <template #center>
        <div
          class="flex items-center gap-2 border rounded-full bg-base-200 p-2 px-4 cursor-pointer"
          v-if="mateStore.currentMate"
          @click="switchMate"
        >
          <UserAvatar :mate="mateStore.currentMate" class="w-5 aspect-[3/4]" />
          {{ mateStore.currentMate?.name || $t("User.mate.unnamed") }}
          <i class="iconify mingcute--down-line" />
        </div>
      </template> -->
      <template #right>
        <button
          v-if="props.enableSelectionMode"
          class="btn btn-primary btn-sm"
          :disabled="!selectedSprite"
          @click="onSelectionConfirm"
        >
          {{ $t("Buttons.confirm") }}
        </button>

        <button class="btn btn-xs" @click="deleteSprite">删除</button>
        <button class="btn btn-xs" @click="onSpriteClick(editingSprite)">
          编辑
        </button>
      </template>
    </ThreadsNav>

    <div v-else class="p-2 flex justify-between gap-2">
      <button
        v-if="!editingSprite?.isDefault"
        class="btn btn-xs rounded btn-outline"
        @click="setDefault"
      >
        {{ $t("Buttons.setDefault") }}
      </button>
      <div v-else class="flex items-center gap-1 text-xs text-base-content/50">
        <i class="iconify mingcute--check-circle-line" />
        {{ $t("Message.default") }}
      </div>
      <div class="flex gap-1">
        <button class="btn btn-xs" @click="deleteSprite">删除</button>
        <button
          class="btn btn-xs btn-primary"
          @click="onSpriteClick(editingSprite)"
        >
          编辑
        </button>
      </div>
    </div>

    <div class="flex flex-col flex-1 overflow-hidden">
      <div
        v-if="loading"
        class="w-full h-full flex justify-center items-center"
      >
        <div class="loading loading-ring loading-lg"></div>
      </div>
      <div v-else class="flex flex-col gap-2 text-base-content/50 h-full">
        <div class="grid grid-rows-[1fr_auto] overflow-y-auto h-full">
          <div class="flex flex-col overflow-hidden relative">
            <div
              class="flex gap-2 overflow-hidden items-center justify-evenly min-h-[152px] h-full flex-1 mate-create-resize-container px-4"
            >
              <!-- 立绘 -->
              <div
                class="flex flex-col gap-1 w-fit min-w-[66%] justify-center items-center"
              >
                <div
                  v-if="editingSprite?.portraitMedia"
                  class="h-full w-full max-w-[280px] relative"
                  @click="
                    modalStore.openMediaPreviewModal([
                      editingSprite.portraitMedia.url,
                    ])
                  "
                >
                  <img
                    :src="usePreviewImg(editingSprite.portraitMedia.url)"
                    alt=""
                    :class="['object-contain h-full']"
                    crossorigin="anonymous"
                  />
                </div>

                <div
                  v-else
                  class="w-[45%] max-h-full"
                  @click="onSpriteClick(editingSprite)"
                >
                  <ImgPortraitPlaceholder :class="['text-secondary']" />
                </div>

                <p class="text-xs text-base-content/50 text-center">
                  在小屋事件中显示
                </p>
              </div>
              <div class="flex flex-col gap-4 w-[30%]">
                <!-- 头像 -->
                <div class="flex flex-col gap-1 justify-center items-center">
                  <div
                    v-if="editingSprite?.avatarMedia"
                    class="flex-1"
                    @click="
                      modalStore.openMediaPreviewModal([
                        editingSprite.avatarMedia.url,
                      ])
                    "
                  >
                    <img
                      :src="usePreviewImg(editingSprite.avatarMedia.url)"
                      alt=""
                      :class="['object-contain w-full']"
                      crossorigin="anonymous"
                    />
                  </div>

                  <div
                    v-else
                    class="flex-1"
                    @click="onSpriteClick(editingSprite)"
                  >
                    <ImgAvatarPlaceholder :class="['text-secondary']" />
                  </div>

                  <p class="text-xs text-base-content/50 text-center">
                    在小屋对话中显示
                  </p>
                </div>

                <div
                  class="mr-1"
                  v-if="editingSprite?.animationzz?.[0]?.media?.url"
                  @click="showMediaPreviewPopup = true"
                >
                  <div
                    class="grid grid-cols-2 w-full gap-x-1.5"
                    :key="editingSprite?.animationzz[0].media?.url"
                  >
                    <div
                      v-for="animation in editingSprite?.animationzz"
                      :key="animation.media?.id"
                      class="flex items-center justify-center"
                    >
                      <SpriteFrame :url="animation.media?.url!" class="w-12" />
                    </div>
                  </div>
                </div>

                <div
                  v-else
                  class="flex-1"
                  @click="onSpriteClick(editingSprite)"
                >
                  <ImgFramePlaceholder :class="['text-secondary']" />
                </div>

                <p class="text-xs text-base-content/50 text-center">
                  在小屋中显示
                </p>
              </div>
            </div>
          </div>

          <div class="overflow-hidden flex-1 flex-shrink-0">
            <div
              class="flex flex-shrink-0 overflow-x-auto overflow-y-hidden px-2 gap-1 mb-2"
            >
              <div
                class="flex flex-col gap-1 w-12 flex-shrink-0"
                @click="clickAddSprite"
              >
                <div
                  class="aspect-[3/4] border-primary-content border-dashed rounded flex justify-center items-center bg-neutral"
                >
                  <div
                    class="w-[60%] aspect-square overflow-hidden flex items-center justify-center border border-dashed rounded border-neutral-content"
                  >
                    <i
                      class="iconify mingcute--add-line text-neutral-content font-light w-3"
                    />
                  </div>
                </div>
                <p class="text-xs text-center">{{ $t(`Buttons.add`) }}</p>
              </div>
              <div
                v-for="item in spriteList"
                :key="item.id"
                :class="[
                  'bg-base-200 rounded-lg border flex-shrink-0',
                  editingSprite?.id === item.id ? 'border-primary' : '',
                ]"
              >
                <div
                  class="relative flex flex-col items-center max-w-12 overflow-hidden"
                  @click="onSpriteClick(item)"
                >
                  <div
                    class="w-12 aspect-[3/4] px-3.5 pt-[18px] text-secondary"
                    v-if="!item.animationzz?.[0]?.media?.url"
                  >
                    <ImgFrameAvatarPlaceholder />
                  </div>
                  <SpriteFrame
                    v-else
                    :url="item.animationzz!.find(v => v.direction === MateSpriteDirection.Down)?.media?.url!"
                    class="w-12 px-2 aspect-[3/4]"
                  />
                  <p
                    class="text-xs text-base-content/50 text-center line-clamp-1 mb-2"
                  >
                    {{ useName(item.name) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <VanPopup
      v-model:show="showMediaPreviewPopup"
      v-if="editingSprite?.animationzz?.length"
    >
      <VanImagePreview
        :images="editingSprite?.animationzz?.map((v) => v.media?.url!)"
        v-model:show="showMediaPreviewPopup"
        :start-position="0"
        class="media-preview"
        closeable
        showIndicators
        teleport="body"
        :close-on-click-image="false"
        @close="showMediaPreviewPopup = false"
      >
        <template #image="{ src }">
          <div class="w-full h-full flex justify-center" :key="src">
            <SpriteFrame class="w-40 bg-transparent" :url="src" />
          </div>
        </template>
      </VanImagePreview>
    </VanPopup>
  </div>
</template>
